import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { coachService, generateCoachLicenseNumber } from '../../services/coachService';
import { refreshCoachLicenseNumber, getCoachGradePrefix } from '../../utils/coachLicenseNumber';
import { TANZANIA_REGION_OPTIONS } from '../../utils/tanzaniaRegions';
import {
  calculateLicenseExpirationDate,
  formatValidityMonthsLabel,
  getDefaultLicenseFields,
  parseValidityMonths,
} from '../../utils/coachLicense';
import CoachDocumentsSection from '../coach-registration/components/CoachDocumentsSection';
import { getCoachGradeOptions } from '../../constants/coachGrades';
import { DEFAULT_NATIONALITY, nationalityOptions } from '../../data/nationalities';
import { useCoachEmailCheck } from '../../hooks/useCoachEmailCheck';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const CoachSelfRegistrationPage = () => {
  const { token } = useParams();
  const isPublicRegistration = token === 'public';
  const [newCertificateFiles, setNewCertificateFiles] = useState([]);
  const { emailStatus, scheduleEmailCheck, checkEmail } = useCoachEmailCheck();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [inviteMeta, setInviteMeta] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    coachName: '',
    grade: '',
    region: '',
    nationality: DEFAULT_NATIONALITY,
    gender: '',
    mobile: '',
    email: '',
    physicalAddress: '',
    dateOfBirth: '',
    licenseNumber: '',
    ...getDefaultLicenseFields(),
    photoFile: null,
    photoFileName: '',
    passportFile: null,
    passportFileName: '',
  });

  const gradeOptions = useMemo(
    () => getCoachGradeOptions(formData.grade),
    [formData.grade]
  );

  useEffect(() => {
    document.title = 'Coach Registration - Tanzania Basketball Federation';
  }, []);

  useEffect(() => {
    const loadInvite = async () => {
      setIsLoading(true);
      setInviteError(null);
      try {
        if (isPublicRegistration) {
          setInviteMeta({ valid: true, public: true });
          setIsLoading(false);
          return;
        }
        const invite = await coachService.validateSelfRegistrationToken(token);
        setInviteMeta(invite);
        setFormData((prev) => ({
          ...prev,
          email: invite.email || prev.email,
          coachName: invite.coachName || prev.coachName,
        }));
      } catch (err) {
        setInviteError(err.message || 'This registration link is invalid or expired.');
      } finally {
        setIsLoading(false);
      }
    };
    if (token) loadInvite();
  }, [token, isPublicRegistration]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'validityPeriod') {
        next.licenseExpirationDate = calculateLicenseExpirationDate(value);
      }
      if (field === 'grade') {
        next.licenseNumber = refreshCoachLicenseNumber(prev.licenseNumber, value);
      }
      return next;
    });
    if (field === 'email') {
      if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
      scheduleEmailCheck(value);
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (submitError) setSubmitError(null);
  };

  const handlePhotoChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      photoFile: file,
      photoFileName: file?.name || '',
    }));
  };

  const handlePassportChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      passportFile: file,
      passportFileName: file?.name || '',
    }));
  };

  const handleAddCertificates = (files) => {
    if (!files?.length) return;
    setNewCertificateFiles((prev) => [...prev, ...files]);
    if (errors.certificateFile) setErrors((prev) => ({ ...prev, certificateFile: '' }));
  };

  const handleRemoveNewCertificate = (index) => {
    setNewCertificateFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.coachName?.trim()) nextErrors.coachName = 'Coach name is required';
    if (!formData.grade) nextErrors.grade = 'Grade is required';
    if (!formData.region) nextErrors.region = 'Region is required';
    if (!formData.nationality) nextErrors.nationality = 'Nationality is required';
    if (!formData.gender) nextErrors.gender = 'Gender is required';
    if (!formData.mobile?.trim()) nextErrors.mobile = 'Mobile number is required';
    if (!formData.email?.trim()) nextErrors.email = 'Email is required';
    else if (emailStatus.available === false) {
      nextErrors.email = emailStatus.message || 'This email is already registered.';
    }
    if (!formData.physicalAddress?.trim()) nextErrors.physicalAddress = 'Physical address is required';
    if (!formData.dateOfBirth) nextErrors.dateOfBirth = 'Birth date is required';
    const validityMonths = parseValidityMonths(formData.validityPeriod);
    if (!validityMonths || validityMonths < 1) {
      nextErrors.validityPeriod = 'Validity period must be at least 1 month';
    }
    if (!formData.licenseExpirationDate) {
      nextErrors.licenseExpirationDate = 'License expiration date could not be calculated';
    }
    if (newCertificateFiles.length < 1) {
      nextErrors.certificateFile = 'At least one coaching certificate is required';
    }
    if (!formData.licenseNumber?.trim() && !formData.grade) {
      nextErrors.licenseNumber = 'Select a grade to generate a license number';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailCheck = await checkEmail(formData.email);
    if (emailCheck.available === false) {
      setErrors((prev) => ({
        ...prev,
        email: emailCheck.message || 'This email is already registered.',
      }));
      return;
    }
    if (emailCheck.available !== true) {
      setErrors((prev) => ({
        ...prev,
        email: 'Please enter a valid email and wait for availability confirmation.',
      }));
      return;
    }
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await coachService.submitSelfRegistration(token, {
        ...formData,
        licenseNumber: formData.licenseNumber.trim() || generateCoachLicenseNumber(formData.grade),
        certificateFiles: newCertificateFiles,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/assets/images/login-background.png)' }}
      >
        <div className="absolute inset-0 bg-white/45" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="w-full py-6 px-4 lg:px-6">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/images/tbf-logo-transparent.png"
                alt="Tanzania Basketball Federation"
                className="h-12 w-auto object-contain drop-shadow-sm"
              />
              <div>
                <h1 className="text-lg font-bold text-white">Coach Registration</h1>
                <p className="text-xs text-white/90">Tanzania Basketball Federation</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-white hover:text-white/80" asChild>
              <Link to="/login">Login</Link>
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 py-8">
          <div className="max-w-3xl mx-auto">
            {isLoading ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center card-shadow">
                <Icon name="Loader2" size={32} className="text-primary mx-auto mb-4 animate-spin" />
                <p className="text-muted-foreground">Validating registration link...</p>
              </div>
            ) : inviteError ? (
              <div className="bg-card border border-border rounded-xl p-8 card-shadow text-center space-y-4">
                <Icon name="AlertTriangle" size={40} className="text-destructive mx-auto" />
                <h2 className="text-xl font-semibold text-foreground">Registration Link Unavailable</h2>
                <p className="text-muted-foreground">{inviteError}</p>
                <p className="text-sm text-muted-foreground">
                  Please contact TBF administration to request a new registration link.
                </p>
              </div>
            ) : submitted ? (
              <div className="bg-card border border-border rounded-xl p-8 card-shadow text-center space-y-4">
                <Icon name="CheckCircle2" size={40} className="text-success mx-auto" />
                <h2 className="text-xl font-semibold text-foreground">Registration Submitted</h2>
                <p className="text-muted-foreground">
                  Your coaching registration has been submitted successfully. A confirmation email
                  with your submitted details has been sent to <strong>{formData.email}</strong>.
                </p>
                <p className="text-sm text-muted-foreground">
                  Your license is pending admin approval. This registration link has now expired.
                </p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-6 md:p-8 card-shadow space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">New Coach Information</h2>
                  <p className="text-muted-foreground mt-1">
                    {isPublicRegistration
                      ? 'Complete your coaching profile using the public TBF registration form.'
                      : 'Complete your coaching profile. This link expires once you submit your registration.'}
                  </p>
                  {inviteMeta?.expiresAt && !isPublicRegistration && (
                    <p className="text-xs text-warning mt-2">
                      Link valid until {new Date(inviteMeta.expiresAt).toLocaleString()}
                    </p>
                  )}
                </div>

                {submitError && (
                  <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <section className="space-y-4">
                    <h3 className="text-lg font-semibold border-b border-border pb-2">Coaching Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Coach Name" value={formData.coachName} onChange={(e) => handleInputChange('coachName', e.target.value)} error={errors.coachName} required />
                      <Select label="Grade" placeholder="Please Select" options={gradeOptions} value={formData.grade} onChange={(v) => handleInputChange('grade', v)} error={errors.grade} required />
                      <Select label="Region" placeholder="Please Select" options={TANZANIA_REGION_OPTIONS} value={formData.region} onChange={(v) => handleInputChange('region', v)} error={errors.region} required />
                      <Select label="Gender" placeholder="Please Select" options={GENDER_OPTIONS} value={formData.gender} onChange={(v) => handleInputChange('gender', v)} error={errors.gender} required />
                      <Select label="Nationality" placeholder="Please Select" options={nationalityOptions} value={formData.nationality} onChange={(v) => handleInputChange('nationality', v)} error={errors.nationality} required />
                    </div>
                    <CoachDocumentsSection
                      photoFileName={formData.photoFileName}
                      onPhotoChange={handlePhotoChange}
                      existingCertificates={[]}
                      newCertificateFiles={newCertificateFiles}
                      onAddCertificates={handleAddCertificates}
                      onRemoveExistingCertificate={() => {}}
                      onRemoveNewCertificate={handleRemoveNewCertificate}
                      existingPassport={null}
                      passportFileName={formData.passportFileName}
                      onPassportChange={handlePassportChange}
                      errors={errors}
                    />
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-lg font-semibold border-b border-border pb-2">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Mobile" type="tel" value={formData.mobile} onChange={(e) => handleInputChange('mobile', e.target.value)} error={errors.mobile} required />
                      <div>
                        <Input
                          label="Email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          error={errors.email || (emailStatus.available === false ? emailStatus.message : '')}
                          required
                          disabled={!isPublicRegistration}
                        />
                        {emailStatus.checking && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Icon name="Loader2" size={12} className="animate-spin" />
                            Checking email availability...
                          </p>
                        )}
                        {!emailStatus.checking && emailStatus.available === true && formData.email?.trim() && (
                          <p className="text-xs text-success mt-1">{emailStatus.message || 'Email is available.'}</p>
                        )}
                      </div>
                      <Input label="Physical Address" value={formData.physicalAddress} onChange={(e) => handleInputChange('physicalAddress', e.target.value)} error={errors.physicalAddress} required />
                      <Input label="Birth Date" type="date" value={formData.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} error={errors.dateOfBirth} required />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-lg font-semibold border-b border-border pb-2">License Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="License Number"
                        value={formData.licenseNumber}
                        onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                        description={
                          formData.grade
                            ? `Auto-generated for ${formData.grade} (e.g. TBF-${getCoachGradePrefix(formData.grade)}-${new Date().getFullYear()}-XXXX)`
                            : 'Select a grade to generate the license number'
                        }
                        required
                      />
                      <Input
                        label="Validity Period (months)"
                        type="number"
                        min={1}
                        max={120}
                        value={formData.validityPeriod}
                        onChange={(e) => handleInputChange('validityPeriod', e.target.value)}
                        error={errors.validityPeriod}
                        required
                      />
                      <Input
                        label="License Expiration Date"
                        type="date"
                        value={formData.licenseExpirationDate}
                        readOnly
                        disabled
                        description={`Auto-calculated from ${formatValidityMonthsLabel(formData.validityPeriod)} from today`}
                        error={errors.licenseExpirationDate}
                        required
                      />
                    </div>
                  </section>

                  <div className="flex justify-end">
                    <Button type="submit" loading={isSubmitting} iconName="Send">
                      Submit Registration
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CoachSelfRegistrationPage;
